"use client"

import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, X } from 'lucide-react'
type SearchProduct = {
  id: string
  name: string
  description: string
  price: number
  originalPrice?: number
  images: string[]
  category: string
  categories?: string[]
  stock: number
  rating: number
  averageRating?: number
  totalRatings?: number
  numRatings?: number
  reviews?: number
  features?: string[]
  tags?: string[]
  material?: string
  dimensions?: unknown
  warranty?: string
  isFeatured?: boolean
  isNewArrival?: boolean
  isBestSeller?: boolean
  createdAt?: string
}
import { collection, getDocs, query as firestoreQuery, where } from 'firebase/firestore'
import { db } from '../../firebase/config'

// Helper to slugify strings for URLs - make sure it matches ProductCard
function slugify(str: string | undefined | null): string {
  if (!str) return '';
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// Simplified searchProducts function with detailed logging
const searchProducts = async (searchTerm: string): Promise<SearchProduct[]> => {
  try {
    console.log('🔍 Starting search for:', searchTerm)
    
    const productsRef = collection(db, "products")
    const productsQuery = firestoreQuery(productsRef, where("isActive", "==", true))
    const productsSnapshot = await getDocs(productsQuery)
    
    console.log('📦 Total products found:', productsSnapshot.docs.length)
    
    const allProducts: SearchProduct[] = []
    
    productsSnapshot.forEach((doc) => {
      const data = doc.data()
      console.log('🏷️ Processing product:', doc.id, data.name)
      
      // Simplified product structure - matching exactly what we need
      const product: SearchProduct = {
        id: doc.id, // This is the Firestore document ID
        name: data.name || "",
        description: data.description || "",
        price: Number(data.price) || 0,
        originalPrice: data.originalPrice ? Number(data.originalPrice) : undefined,
        images: Array.isArray(data.images) ? data.images : (data.image ? [data.image] : []),
        category: data.category || data.categories?.[0] || 'furniture',
        categories: Array.isArray(data.categories) ? data.categories : [data.category || 'furniture'],
        stock: Number(data.stock) || 0,
        rating: Number(data.rating) || Number(data.averageRating) || 0,
        averageRating: Number(data.averageRating) || Number(data.rating) || 0,
        totalRatings: Number(data.totalRatings) || Number(data.numRatings) || Number(data.reviews) || 0,
        numRatings: Number(data.numRatings) || Number(data.totalRatings) || Number(data.reviews) || 0,
        reviews: Number(data.reviews) || Number(data.totalRatings) || Number(data.numRatings) || 0,
        features: Array.isArray(data.features) ? data.features : [],
        tags: Array.isArray(data.tags) ? data.tags : [],
        material: data.material || undefined,
        dimensions: data.dimensions || undefined,
        warranty: data.warranty || undefined,
        isFeatured: Boolean(data.isFeatured),
        isNewArrival: Boolean(data.isNewArrival),
        isBestSeller: Boolean(data.isBestSeller),
        createdAt: data.createdAt || undefined,
      }
      
      console.log('✅ Processed product:', {
        id: product.id,
        name: product.name,
        image: product.images?.[0] || ""
      })
      
      allProducts.push(product)
    })

    // Filter products based on search term
    const searchTermLower = searchTerm.toLowerCase().trim()
    console.log('🔍 Filtering with term:', searchTermLower)
    
    const filteredProducts = allProducts.filter((product) => {
      const nameMatch = product.name?.toLowerCase().includes(searchTermLower)
      const descriptionMatch = product.description?.toLowerCase().includes(searchTermLower)
      const categoryMatch = product.category?.toLowerCase().includes(searchTermLower)
      const materialMatch = product.material?.toLowerCase().includes(searchTermLower)
      
      const matches = nameMatch || descriptionMatch || categoryMatch || materialMatch
      
      if (matches) {
        console.log('✅ Product matches:', product.name)
      }
      
      return matches
    })

    console.log('📊 Search results:', filteredProducts.length, 'products found')
    return filteredProducts
    
  } catch (error) {
    console.error('❌ Error searching products:', error)
    throw error
  }
}

export interface SearchBarProps {
  className?: string
  placeholder?: string
  onSearch?: () => void
}

const SearchBar: React.FC<SearchBarProps> = ({ 
  className = '', 
  placeholder = 'Search for furniture...', 
  onSearch 
}) => {
  const [query, setQuery] = useState('')
  const [suggestions, setSuggestions] = useState<SearchProduct[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()
  const searchRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Debounce search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (query.trim().length > 2) {
        fetchSuggestions(query)
      } else {
        setSuggestions([])
        setIsOpen(false)
      }
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [query])

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const fetchSuggestions = async (searchTerm: string) => {
    console.log('🔄 Fetching suggestions for:', searchTerm)
    setIsLoading(true)
    try {
      const results = await searchProducts(searchTerm)
      console.log('📋 Setting suggestions:', results.length, 'items')
      setSuggestions(results.slice(0, 8)) // Limit to 8 suggestions
      setIsOpen(results.length > 0)
    } catch (error) {
      console.error('❌ Error fetching suggestions:', error)
      setSuggestions([])
      setIsOpen(false)
    } finally {
      setIsLoading(false)
    }
  }

  // Generate product URL exactly like ProductCard does
  const generateProductUrl = (product: SearchProduct) => {
    // Use the first category from categories array, or fallback to category field
    const primaryCategory = product.categories?.[0] || product.category || 'furniture';
    const categorySlug = slugify(primaryCategory);
    const nameSlug = slugify(product.name) || product.id;
    
    return `/${categorySlug}/${nameSlug}`;
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (query.trim()) {
      console.log('🔍 Submitting search:', query.trim())
      navigate(`/search?q=${encodeURIComponent(query.trim())}`)
      setIsOpen(false)
      inputRef.current?.blur()
      onSearch?.()
    }
  }

  const handleSuggestionClick = (product: SearchProduct) => {
    console.log('🖱️ Product clicked:', {
      id: product.id,
      name: product.name,
      categories: product.categories,
      category: product.category
    })
    
    // Generate URL same way as ProductCard
    const navigationPath = generateProductUrl(product)
    
    console.log('🌍 Current URL:', window.location.href)
    console.log('🎯 Navigating to product:', {
      id: product.id,
      name: product.name,
      path: navigationPath
    })
    
    try {
      navigate(navigationPath)
      console.log('✅ Navigation successful to:', navigationPath)
      
      // Clear search
      setQuery('')
      setIsOpen(false)
      inputRef.current?.blur()
    } catch (error) {
      console.error('❌ Navigation failed:', error)
    }
  }

  const clearSearch = () => {
    setQuery('')
    setSuggestions([])
    setIsOpen(false)
    inputRef.current?.focus()
  }

  const highlightMatch = (text: string, query: string) => {
    if (!query || !text) return text

    const regex = new RegExp(`(${query})`, 'gi')
    const parts = text.split(regex)

    return parts.map((part, index) =>
      regex.test(part) ? (
        <span key={index} className="bg-yellow-200 font-semibold">
          {part}
        </span>
      ) : (
        part
      ),
    )
  }

  return (
    <div ref={searchRef} className={`relative ${className}`}>
      <form onSubmit={handleSubmit} className="relative">
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              console.log('📝 Query changed:', e.target.value)
              setQuery(e.target.value)
            }}
            onFocus={() => {
              console.log('🎯 Input focused, suggestions:', suggestions.length)
              if (suggestions.length > 0) {
                setIsOpen(true)
              }
            }}
            placeholder={placeholder}
            className="w-full pl-4 pr-12 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />

          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center space-x-1">
            {query && (
              <button type="button" onClick={clearSearch} className="text-gray-400 hover:text-gray-600 p-1">
                <X className="w-4 h-4" />
              </button>
            )}

            <button type="submit" className="text-gray-400 hover:text-primary-600 p-1">
              <Search className="w-4 h-4" />
            </button>
          </div>
        </div>
      </form>

      {/* Suggestions Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
          {isLoading ? (
            <div className="p-4 text-center text-gray-500">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-sm">Searching...</p>
            </div>
          ) : suggestions.length > 0 ? (
            <>
              <div className="p-2 border-b border-gray-100">
                <p className="text-xs text-gray-500 font-medium">
                  {suggestions.length} result{suggestions.length !== 1 ? 's' : ''} found
                </p>
              </div>

              <div className="py-2">
                {suggestions.map((product) => {
                  console.log('🎨 Rendering product:', product.id, product.name)
                  return (
                    <button
                      key={product.id}
                      onClick={() => {
                        console.log('🖱️ Button clicked for product:', product.id)
                        handleSuggestionClick(product)
                      }}
                      className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center space-x-3 transition-colors"
                    >
                      <img
                        src={product.images?.[0] || '/placeholder.svg'}
                        alt={product.name}
                        className="w-12 h-12 object-cover rounded-lg flex-shrink-0"
                        onError={(e) => {
                          console.log('🖼️ Image error for product:', product.id)
                          const target = e.target as HTMLImageElement
                          target.src = '/placeholder.svg'
                        }}
                      />

                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-gray-900 truncate">
                          {highlightMatch(product.name, query)}
                        </h4>
                        <p className="text-sm text-gray-500 capitalize">{product.category}</p>
                        <p className="text-sm font-semibold text-primary-600">
                          ₹{product.price?.toLocaleString() || 0}
                        </p>
                        {/* Debug info */}
                        <p className="text-xs text-gray-400">
                          Category: {product.categories?.[0] || product.category} | Slug: {slugify(product.name)}
                        </p>
                      </div>

                      <div className="flex-shrink-0">
                        <div className="flex items-center">
                          <span className="text-yellow-400">★</span>
                          <span className="text-sm text-gray-600 ml-1">{product.rating || 0}</span>
                        </div>
                      </div>
                    </button>
                  )
                })}
              </div>

              {query && (
                <div className="border-t border-gray-100 p-2">
                  <button
                    onClick={handleSubmit}
                    className="w-full px-4 py-2 text-left text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                  >
                    <div className="flex items-center space-x-2">
                      <Search className="w-4 h-4" />
                      <span>Search for "{query}"</span>
                    </div>
                  </button>
                </div>
              )}
            </>
          ) : query.trim().length > 2 ? (
            <div className="p-4 text-center text-gray-500">
              <Search className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No products found for "{query}"</p>
              <p className="text-xs text-gray-400 mt-1">Try different keywords or check spelling</p>
            </div>
          ) : null}
        </div>
      )}
    </div>
  )
}

export default SearchBar
